import {useCallback, useContext, useState} from "react";
import {compareProductDisplayOrders} from "../../../../utils/supply";
import {ScreenStatusContext} from "../../../../context/ScreenStatusContext";
import {getDisplaySize} from "../../../../utils/Items";


/**
 * 発注情報管理hook
 */
const useSupplyForm = () => {

    const [targetSupplier, setTargetSupplier] = useState({});
    const [supplyForms, setSupplyForms] = useState([]);
    const {setChanged} = useContext(ScreenStatusContext);


    const changeOrigin = useCallback((supplyProductIndex, newOrigin) => {
        setSupplyForms((prev) => {
            const newForms = prev.map((sp, spIndex) => {
                if (spIndex === supplyProductIndex) {
                    return {
                        ...sp,
                        originId: newOrigin?.originId,
                        originName: newOrigin?.originName,
                        productDetailId: null,
                    };
                } else {
                    return { ...sp };
                }
            });

            setChanged(true);
            return newForms;
        });
    }, [setChanged]);

    const changeSupplyQuantityDetailsAt = useCallback((supplyProductIndex) => (supplyQuantityDetails) => {

        setSupplyForms((prev) =>
            prev.map((sp, spIndex) =>
                spIndex == supplyProductIndex
                    ? {
                        ...sp,
                        supplyQuantityDetails: supplyQuantityDetails.map((spd, index) => ({...spd, supplyDetailIndex: index}))
                    }
                    : {...sp}
            )
        );

        setChanged(true);
    }, [setChanged, setSupplyForms]);

    const setSupplyInfo = (supplyInfo) => {
        setTargetSupplier(supplyInfo.targetSupplier);
        setSupplyForms(
            supplyInfo.supplyDetails.map((supplyProduct, supplyProductIndex) => ({
                ...supplyProduct,
                otherSpec: supplyProduct.otherSpec ?? null,
                supplyProductIndex,
                displaySize: getDisplaySize(
                    supplyProduct?.rankName,
                    supplyProduct?.productClassName,
                    supplyProduct?.weight,
                    supplyProduct?.weightUnit || supplyProduct?.weightUnitName,
                    supplyProduct?.packingName,
                    supplyProduct?.otherSpec
                ),
                supplyQuantityDetails:
                    createSupplyDetailsForForm(supplyProduct.supplyQuantityDetails)
            }))
        );
        setChanged(false);
    }

    const addSupplyDetail = (supplyProduct) => {

        setSupplyForms((prev) => {
            const maxDisplayOrder = prev.reduce((max, sp) => Math.max(max, sp.displayOrder ?? 0), 0);
            const insertedList
                = [
                ...prev,
                {
                    ...supplyProduct,
                    otherSpec: supplyProduct.otherSpec ?? null,
                    displaySize: getDisplaySize(
                        supplyProduct?.rankName,
                        supplyProduct?.productClassName,
                        supplyProduct?.weight,
                        supplyProduct?.weightUnit || supplyProduct?.weightUnitName,
                        supplyProduct?.packingName,
                        supplyProduct?.otherSpec
                    ),
                    supplyQuantityDetails:
                        createSupplyDetailsForForm(supplyProduct.supplyQuantityDetails),
                    isAddedFromDialog: true,
                    displayOrder: maxDisplayOrder + 1
                }
            ];

            insertedList.sort(compareProductDisplayOrders);

            return insertedList.map((sp, supplyProductIndex) => ({...sp, supplyProductIndex}))
        })

        setChanged(true);
    }

    /**
     * 発注入力済み商品があるか？
     * @returns {Boolean} 発注入力済み商品があるか？
     */
    const hasProductWithSupply = () => {
        return supplyForms.some((supplyProduct) =>
            supplyProduct.supplyQuantityDetails.some((detail) => detail.supplyQuantity > 0)
        )
    };

    const hasError = () => {
        return supplyForms.some((supplyProduct) =>
            supplyProduct.supplyQuantityDetails.some((detail) => detail.error)
        )
    };

    /* ────────── ソート操作（ドラッグ&ドロップ） ────────── */
    const moveSupplyDetail = useCallback((dragIndex, hoverIndex) => {
        setSupplyForms((prev) => {
            const newList = [...prev];
            const [draggedItem] = newList.splice(dragIndex, 1);
            newList.splice(hoverIndex, 0, draggedItem);
            // Re-index supplyProductIndex and displayOrder
            return newList.map((sp, index) => ({
                ...sp,
                supplyProductIndex: index,
                displayOrder: index + 1,
            }));
        });
        setChanged(true);
    }, [setChanged]);

    /* ────────── 非表示/表示操作（フロントエンドのみ） ────────── */
    const hideSupplyDetail = useCallback((product) => {
        // isDelete フラグを立てて非表示にする（リストからは削除しない）
        setSupplyForms((prev) => {
            return prev.map((sp) => {
                if (sp.supplyProductIndex !== product.supplyProductIndex) return sp;
                return {
                    ...sp,
                    isDelete: true,
                    supplyQuantityDetails: sp.supplyQuantityDetails.map((sqd) => ({
                        ...sqd,
                        isDelete: true,
                    })),
                };
            });
        });
        setChanged(true);
    }, [setChanged]);

    const showSupplyDetail = useCallback((product) => {
        const productKey = makeSupplyDetailKey(product);
        // isDelete フラグを解除して再表示する
        setSupplyForms((prev) => {
            // 既に supplyForms に isDelete: true で残っているか確認（最初の1件のみ復元）
            const matchIndex = prev.findIndex((sp) =>
                makeSupplyDetailKey(sp) === productKey && sp.isDelete
            );

            if (matchIndex >= 0) {
                // isDelete フラグを解除
                return prev.map((sp, i) => {
                    if (i !== matchIndex) return sp;
                    return {
                        ...sp,
                        isDelete: false,
                        supplyQuantityDetails: sp.supplyQuantityDetails.map((sqd) => ({
                            ...sqd,
                            isDelete: false,
                        })),
                    };
                });
            }

            // supplyForms にない場合（履歴から新規追加）
            const maxDisplayOrder = prev.reduce((max, sp) => Math.max(max, sp.displayOrder ?? 0), 0);
            const newList = [...prev, {
                ...product,
                otherSpec: product.otherSpec ?? null,
                isDelete: false,
                displaySize: getDisplaySize(
                    product?.rankName,
                    product?.productClassName,
                    product?.weight,
                    product?.weightUnit || product?.weightUnitName,
                    product?.packingName,
                    product?.otherSpec
                ),
                supplyQuantityDetails: createSupplyDetailsForForm(product.supplyQuantityDetails ?? []),
                isAddedFromHistory: true,
                displayOrder: product.displayOrder ?? (maxDisplayOrder + 1),
            }];
            newList.sort(compareProductDisplayOrders);
            return newList.map((sp, index) => ({
                ...sp,
                supplyProductIndex: index,
            }));
        });
        setChanged(true);
    }, [setChanged]);

    const addSupplyDetailAt = useCallback((supplyProduct, atIndex) => {
        setSupplyForms((prev) => {
            const newItem = {
                ...supplyProduct,
                otherSpec: supplyProduct.otherSpec ?? null,
                displaySize: getDisplaySize(
                    supplyProduct?.rankName,
                    supplyProduct?.productClassName,
                    supplyProduct?.weight,
                    supplyProduct?.weightUnit || supplyProduct?.weightUnitName,
                    supplyProduct?.packingName,
                    supplyProduct?.otherSpec
                ),
                supplyQuantityDetails: createSupplyDetailsForForm(supplyProduct.supplyQuantityDetails ?? []),
                isAddedFromDialog: true,
            };
            const newList = [...prev];
            newList.splice(atIndex, 0, newItem);
            return newList.map((sp, index) => ({
                ...sp,
                supplyProductIndex: index,
                displayOrder: index + 1,
            }));
        });
        setChanged(true);
    }, [setChanged]);

    const showSupplyDetailAt = useCallback((product, atIndex) => {
        const productKey = makeSupplyDetailKey(product);
        setSupplyForms((prev) => {
            // 既に isDelete: true で残っているか確認
            const matchIndex = prev.findIndex((sp) =>
                makeSupplyDetailKey(sp) === productKey && sp.isDelete
            );

            if (matchIndex >= 0) {
                // isDelete フラグを解除して指定位置に移動
                const restored = {
                    ...prev[matchIndex],
                    isDelete: false,
                    supplyQuantityDetails: prev[matchIndex].supplyQuantityDetails.map((sqd) => ({
                        ...sqd,
                        isDelete: false,
                    })),
                };
                const withoutMatch = prev.filter((_, i) => i !== matchIndex);
                withoutMatch.splice(atIndex > matchIndex ? atIndex - 1 : atIndex, 0, restored);
                return withoutMatch.map((sp, index) => ({
                    ...sp,
                    supplyProductIndex: index,
                    displayOrder: index + 1,
                }));
            }

            // supplyForms にない場合（履歴から新規追加）
            const newItem = {
                ...product,
                otherSpec: product.otherSpec ?? null,
                displaySize: getDisplaySize(
                    product?.rankName,
                    product?.productClassName,
                    product?.weight,
                    product?.weightUnit || product?.weightUnitName,
                    product?.packingName,
                    product?.otherSpec
                ),
                supplyQuantityDetails: createSupplyDetailsForForm(product.supplyQuantityDetails ?? []),
                isAddedFromHistory: true,
            };
            const newList = [...prev];
            newList.splice(atIndex, 0, newItem);
            return newList.map((sp, index) => ({
                ...sp,
                supplyProductIndex: index,
                displayOrder: index + 1,
            }));
        });
        setChanged(true);
    }, [setChanged]);

    return {
        supplyForm: {
            forms: supplyForms,
            changeSupplyQuantityDetailsAt,
            changeOrigin,
            moveSupplyDetail
        },
        targetSupplier,
        setSupplyInfo,
        addSupplyDetail,
        addSupplyDetailAt,
        hasProductWithSupply,
        hasError,
        hideSupplyDetail,
        showSupplyDetail,
        showSupplyDetailAt
    };
}

export default useSupplyForm;


const createSupplyDetailsForForm = (details = []) => {

    let result = [];

        if (details.length > 0) {
            result = details
        } else {
            result =  [{
                    supplyQuantity: null,
                    supplyUnitQuantity: null,
                    remarks: ""
                }];
        };

    return result.map((supplyDetail, supplyDetailIndex) => ({
        ...supplyDetail,
        supplyDetailIndex,
        isValidUnitQuantity: Boolean(supplyDetail.supplyUnitQuantity),
        isDelete: false,
        error: false
    }));

}

/**
 * 同一商品判定キーを生成する（全識別フィールドを使用）
 */
const makeSupplyDetailKey = (item) => [
    item.productId,
    item.supplyTypeId ?? "",
    item.originId ?? "",
    item.rankId ?? "",
    item.productClassId ?? "",
    item.packingId ?? "",
    item.weight ?? "",
    item.weightUnitId ?? "",
].join("-");

